import { Fragment, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useBackOfficeSamples,
    useSetSampleRotation,
    type BackOfficeSample,
} from '@/services/sample.service';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import { DeletedTag } from './BackOfficeProjects';
import AddSampleDialog from './AddSampleDialog';
import { groupByProject, ProjectGroupRow } from './projectGroups';

const MUTED = 'text-[#6b7480]';
const FILTER =
    'w-full max-w-sm bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-2 text-sm text-[#e5e7e3] placeholder-[#6b7480] focus:outline-none focus:border-[#2d6a4f]';

function RotationToggle({ sample }: { sample: BackOfficeSample }) {
    const setRotation = useSetSampleRotation();
    return (
        <button
            onClick={() =>
                setRotation.mutate({
                    sampleId: sample.id,
                    rotateClockwise: !sample.rotateClockwise,
                })
            }
            title="Click to switch direction"
            className={
                'px-3 py-1 rounded-md text-xs font-medium border transition whitespace-nowrap ' +
                (sample.rotateClockwise
                    ? 'bg-[#1e2a22] border-[#2d6a4f] text-[#74c69d]'
                    : 'bg-[#161c22] border-[#1e242a] text-[#a8b0bb] hover:text-white')
            }
        >
            {sample.rotateClockwise ? 'Clockwise ↻' : 'Counter-clockwise ↺'}
        </button>
    );
}

/** One table, its rows grouped by project: the default one first, then by
 *  name. */
export default function BackOfficeSamples() {
    const { data, isLoading, error } = useBackOfficeSamples();
    const [query, setQuery] = useState('');

    if (isLoading)
        return (
            <BackofficePage title="Samples" description="Loading…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Samples" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    const q = query.trim().toLowerCase();
    const ordered = groupByProject(
        (data ?? []).filter(
            (r) =>
                !q ||
                r.name.toLowerCase().includes(q) ||
                r.projectName.toLowerCase().includes(q) ||
                (r.scannerSerialNumber ?? '').toLowerCase().includes(q),
        ),
        (r) => ({ id: r.projectId, name: r.projectName }),
    );

    return (
        <BackofficePage title="Samples" actions={<AddSampleDialog />} wide>
            <div className="mb-4">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by sample, project or scanner…"
                    className={FILTER}
                />
            </div>
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Sample</TH>
                            <TH>Description</TH>
                            <TH>Scanner</TH>
                            <TH align="right">Images</TH>
                            <TH align="center">Rotation</TH>
                            <TH align="right">Depth</TH>
                            <TH align="right">Tilt</TH>
                            <TH>GPS</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {ordered.map((group) => (
                            <Fragment key={group.id}>
                                <ProjectGroupRow
                                    name={group.name}
                                    count={group.rows.length}
                                    colSpan={8}
                                />
                                {group.rows.map((r) => (
                                    <SampleRow key={r.id} r={r} />
                                ))}
                            </Fragment>
                        ))}
                        {ordered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={8}
                                    className={`px-4 py-10 text-center text-sm ${MUTED}`}
                                >
                                    No sample.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}

function SampleRow({ r }: { r: BackOfficeSample }) {
    return (
        <TR className={r.softDelete ? 'opacity-60' : ''}>
            <TD>
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">{r.name}</span>
                    {r.softDelete && <DeletedTag />}
                </div>
            </TD>
            <TD>
                {/* Cut to a line; the whole text on hover. */}
                {r.description ? (
                    <div className="max-w-sm truncate" title={r.description}>
                        {r.description}
                    </div>
                ) : (
                    <span className={MUTED}>—</span>
                )}
            </TD>
            <TD mono className="whitespace-nowrap">
                {r.scannerSerialNumber ?? <span className={MUTED}>—</span>}
            </TD>
            <TD align="right">{r.imageCount}</TD>
            <TD align="center">
                <RotationToggle sample={r} />
            </TD>
            <TD align="right" className="whitespace-nowrap">
                {r.topEdgeDepthCm} cm
            </TD>
            <TD align="right">{r.tiltDeg}°</TD>
            <TD mono className="whitespace-nowrap">
                {r.latitude != null && r.longitude != null ? (
                    `${r.latitude}, ${r.longitude}`
                ) : (
                    <span className={MUTED}>—</span>
                )}
            </TD>
        </TR>
    );
}
